// src/modulos/Dashboard/dashboards/ComercialDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import useFirestore from '../../hooks/useFirestore';

const ComercialDashboard = ({ user }) => {
    const { data: clients, loading: clientsLoading } = useFirestore('clientes', null, null, null, 1000);
    const { data: receivables, loading: receivablesLoading } = useFirestore('financial_receivables', null, null, null, 1000);
    const { data: projects, loading: projectsLoading } = useFirestore('development-projects', 'code', null, null, 1000);

    // ✅ OTIMIZADO: Cálculos memoizados para evitar reprocessamento desnecessário
    const stats = useMemo(() => {
        if (!clients || !receivables) {
            return {
                activeClients: 0,
                upcomingPayments: 0,
                totalReceivables: 0,
                totalClients: 0,
                averageTicket: 0,
                proposalsSent: 0,
                inNegotiation: 0,
                expectedClosing: 0,
                totalOpportunities: 0,
                recentActivities: [],
                projectsNotStarted: []
            };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const activeClients = clients.filter(c => c.status === 'active').length;
        const totalClients = clients.length;

        const pendingReceivables = receivables.filter(r => r.status === 'pending');
        const totalReceivables = pendingReceivables.reduce((sum, r) => sum + (r.amount || 0), 0);
        const averageTicket = pendingReceivables.length > 0 ? totalReceivables / pendingReceivables.length : 0;

        const upcomingPayments = receivables.filter(r => {
            if (!r.dueDate || r.status !== 'pending') return false;
            const dueDate = new Date(r.dueDate);
            const daysDiff = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
            return daysDiff <= 7 && daysDiff >= 0;
        }).length;

        // --- Oportunidades baseadas nos projetos de desenvolvimento ---
        let proposalsSent = 0;
        let inNegotiation = 0;
        let expectedClosing = 0;
        let totalOpportunities = 0;

        if (projects) {
            const activeProjects = projects.filter(p => p.situation === 'Active');
            totalOpportunities = activeProjects.length;

            activeProjects.forEach(project => {
                const steps = project.steps || {};

                if (steps.client?.status === 'sent' || steps.client?.status === 'in_progress') {
                    proposalsSent++;
                }
                else if (steps.formula?.status === 'in_progress' || steps.manipulation?.status === 'in_progress') {
                    inNegotiation++;
                }
                else if (steps.quality?.status === 'in_progress' || steps.quality?.status === 'completed') {
                    expectedClosing++;
                }
                else if (project.situation === 'Active') {
                    inNegotiation++;
                }
            });
        }

        // --- Atividades Recentes (últimos 5 projetos atualizados) ---
        const recentActivities = [];
        if (projects) {
            const sortedProjects = [...projects]
                .filter(p => p.updatedAt)
                .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                .slice(0, 5);

            sortedProjects.forEach(project => {
                const clientName = project.clientName || 'Cliente não informado';
                const projectName = project.projectName || 'Projeto sem nome';
                const date = project.updatedAt ? new Date(project.updatedAt).toLocaleDateString('pt-BR') : '';

                let activityType = '';
                let activityColor = '';
                const steps = project.steps || {};

                if (project.situation === 'Completed') {
                    activityType = 'Projeto concluído';
                    activityColor = '#27AE60';
                } else if (steps.status?.status === 'approved') {
                    activityType = 'Amostra aprovada pelo cliente';
                    activityColor = '#27AE60';
                } else if (steps.status?.status === 'rejected') {
                    activityType = 'Amostra rejeitada pelo cliente';
                    activityColor = '#C0392B';
                } else if (steps.client?.status === 'sent' || steps.client?.status === 'in_progress') {
                    activityType = 'Amostra enviada ao cliente';
                    activityColor = '#007bff';
                } else if (steps.quality?.status === 'completed' || steps.quality?.status === 'in_progress') {
                    activityType = 'Controle de qualidade em andamento';
                    activityColor = '#E67E22';
                } else if (steps.manipulation?.status === 'completed' || steps.manipulation?.status === 'in_progress') {
                    activityType = 'Manipulação em andamento';
                    activityColor = '#E67E22';
                } else if (steps.formula?.status === 'completed' || steps.formula?.status === 'in_progress') {
                    activityType = 'Desenvolvimento de fórmula';
                    activityColor = '#E67E22';
                } else {
                    activityType = 'Projeto em andamento';
                    activityColor = '#6B7B8C';
                }

                recentActivities.push({
                    client: clientName,
                    project: projectName,
                    activity: activityType,
                    date: date,
                    color: activityColor,
                    code: project.code || '',
                    id: project.id
                });
            });
        }

        // --- Projetos NÃO INICIADOS (todos os steps em pending ou vazios) ---
        const projectsNotStarted = [];
        if (projects) {
            // Filtra projetos ativos ou em espera que nunca foram iniciados
            const notStartedProjects = projects.filter(project => {
                // Ignora projetos concluídos ou cancelados
                if (project.situation === 'Completed' || project.situation === 'Cancelled') return false;

                const steps = project.steps || {};

                // Verifica se TODOS os steps estão pending ou vazios
                // Se o projeto não tem steps ou todos estão pending, NÃO foi iniciado
                const stepKeys = Object.keys(steps);

                // Se não tem steps definidos, considera não iniciado
                if (stepKeys.length === 0) return true;

                // Verifica se algum step tem status diferente de 'pending' ou vazio
                const hasAnyProgress = stepKeys.some(stepId => {
                    const step = steps[stepId];
                    if (!step) return false;
                    const status = step.status || '';
                    // Status que indicam progresso: completed, approved, in_progress, sent, etc.
                    return status !== 'pending' && status !== '' && status !== 'not_started';
                });

                // Se não tem nenhum progresso, o projeto não foi iniciado
                return !hasAnyProgress;
            });

            // Ordena por data de criação (mais antigos primeiro)
            notStartedProjects.sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                return dateA - dateB;
            });

            // Pega os 5 mais antigos
            notStartedProjects.slice(0, 5).forEach(project => {
                const clientName = project.clientName || 'Cliente não informado';
                const projectName = project.projectName || 'Projeto sem nome';
                const createdAt = project.createdAt ? new Date(project.createdAt) : new Date();
                const daysSinceCreation = Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24));

                // Calcula idade do projeto
                let age = 'Novo';
                let ageColor = '#27AE60';
                if (daysSinceCreation > 60) {
                    age = 'Muito antigo';
                    ageColor = '#C0392B';
                } else if (daysSinceCreation > 30) {
                    age = 'Antigo';
                    ageColor = '#E67E22';
                } else if (daysSinceCreation > 15) {
                    age = 'Recente';
                    ageColor = '#F39C12';
                }

                // Verifica se tem briefing preenchido
                const briefing = project.briefing || {};
                const hasBriefing = Object.keys(briefing).some(key => {
                    const value = briefing[key];
                    return value && value !== '' && value !== '[]' && !(Array.isArray(value) && value.length === 0);
                });

                // Conta quantos steps existem no projeto
                const totalSteps = Object.keys(project.steps || {}).length;
                const pendingSteps = Object.values(project.steps || {}).filter(s =>
                    s?.status === 'pending' || s?.status === '' || s?.status === 'not_started'
                ).length;

                projectsNotStarted.push({
                    client: clientName,
                    project: projectName,
                    code: project.code || '',
                    id: project.id,
                    daysSinceCreation,
                    age,
                    ageColor,
                    situation: project.situation || 'Active',
                    createdAt: createdAt.toLocaleDateString('pt-BR'),
                    hasBriefing,
                    totalSteps: totalSteps || 0,
                    pendingSteps: pendingSteps || 0,
                    // Verifica se todos os steps estão pendentes
                    allPending: totalSteps > 0 && pendingSteps === totalSteps
                });
            });
        }

        return {
            activeClients,
            upcomingPayments,
            totalReceivables,
            totalClients,
            averageTicket,
            proposalsSent,
            inNegotiation,
            expectedClosing,
            totalOpportunities,
            recentActivities,
            projectsNotStarted
        };
    }, [clients, receivables, projects]);

    const isLoading = clientsLoading || receivablesLoading || projectsLoading;

    if (isLoading && !clients && !receivables && !projects) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '3px solid #f3f4f6', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p style={{ marginTop: '16px', color: '#6b7280' }}>Carregando dados comerciais...</p>
            </div>
        );
    }

    return (
        <div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '30px' }}>
                {/* Card 1: Base de Clientes */}
                <div style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff' }}>
                    <h3>👥 Base de Clientes</h3>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', marginTop: '10px' }}>
                        {stats.activeClients}
                    </div>
                    <p>Clientes ativos de {stats.totalClients} total</p>
                    <button
                        style={{
                            marginTop: '15px', padding: '8px 16px', cursor: 'pointer', width: '100%',
                            backgroundColor: '#0B1F38', color: '#fff', border: 'none', borderRadius: '4px'
                        }}
                        onClick={() => window.location.href = '/clientes'}
                    >
                        Ver Lista Completa
                    </button>
                </div>

                {/* Card 2: Atividades Recentes */}
                <div style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '8px', backgroundColor: '#fff' }}>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}></span> Atividades Recentes
                        <span style={{
                            marginLeft: 'auto',
                            fontSize: '12px',
                            backgroundColor: '#E8EAED',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            color: '#4A5A6B'
                        }}>
                            {stats.recentActivities.length}
                        </span>
                    </h3>
                    {stats.recentActivities.length > 0 ? (
                        <ul style={{ marginTop: '10px', paddingLeft: '0', listStyle: 'none' }}>
                            {stats.recentActivities.map((activity, index) => (
                                <li key={index} style={{
                                    marginBottom: '10px',
                                    borderBottom: index < stats.recentActivities.length - 1 ? '1px solid #eee' : 'none',
                                    paddingBottom: '10px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                        <span style={{
                                            display: 'inline-block',
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            backgroundColor: activity.color,
                                            marginTop: '4px',
                                            flexShrink: 0
                                        }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '600', fontSize: '14px' }}>
                                                {activity.client}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#4A5A6B' }}>
                                                {activity.activity}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                                                {activity.date} {activity.code && `• ${activity.code}`}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p style={{ fontSize: '14px', color: '#999', marginTop: '10px', textAlign: 'center', padding: '20px 0' }}>
                            Nenhuma atividade recente
                        </p>
                    )}
                    <button
                        style={{
                            marginTop: '15px', padding: '8px 16px', cursor: 'pointer', width: '100%',
                            backgroundColor: '#0B1F38', color: '#fff', border: 'none', borderRadius: '4px'
                        }}
                        onClick={() => window.location.href = '/projeto/desenvolvimento'}
                    >
                        Ver Todos os Projetos
                    </button>
                </div>

                {/* Card 3: Projetos Não Iniciados */}
                <div style={{
                    padding: '20px',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    borderLeft: stats.projectsNotStarted.length > 0 ? '4px solid #E67E22' : '4px solid #27AE60'
                }}>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '20px' }}></span> Projetos Não Iniciados
                        <span style={{
                            marginLeft: 'auto',
                            fontSize: '12px',
                            backgroundColor: stats.projectsNotStarted.length > 0 ? '#FEF5E7' : '#EAF5EE',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            color: stats.projectsNotStarted.length > 0 ? '#E67E22' : '#27AE60'
                        }}>
                            {stats.projectsNotStarted.length}
                        </span>
                    </h3>
                    {stats.projectsNotStarted.length > 0 ? (
                        <ul style={{ marginTop: '10px', paddingLeft: '0', listStyle: 'none' }}>
                            {stats.projectsNotStarted.map((project, index) => (
                                <li key={index} style={{
                                    marginBottom: '10px',
                                    borderBottom: index < stats.projectsNotStarted.length - 1 ? '1px solid #eee' : 'none',
                                    paddingBottom: '10px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                        <span style={{
                                            display: 'inline-block',
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            backgroundColor: project.ageColor,
                                            marginTop: '4px',
                                            flexShrink: 0
                                        }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '600', fontSize: '14px' }}>
                                                {project.project}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#4A5A6B' }}>
                                                {project.client} • {project.code}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                marginTop: '4px',
                                                flexWrap: 'wrap'
                                            }}>
                                                <span style={{
                                                    fontSize: '12px',
                                                    color: '#999',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    📅 Criado há {project.daysSinceCreation} dias
                                                </span>
                                                <span style={{
                                                    fontSize: '11px',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    backgroundColor: project.ageColor + '20',
                                                    color: project.ageColor,
                                                    fontWeight: '500'
                                                }}>
                                                    {project.age}
                                                </span>
                                                {!project.hasBriefing && (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        backgroundColor: '#FDF0EE',
                                                        color: '#C0392B',
                                                        fontWeight: '500'
                                                    }}>
                                                        ⚠️ Sem briefing
                                                    </span>
                                                )}
                                                {project.totalSteps > 0 && project.allPending && (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        backgroundColor: '#E8EAED',
                                                        color: '#4A5A6B',
                                                        fontWeight: '500'
                                                    }}>
                                                        📋 {project.totalSteps} steps pendentes
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: '20px 0',
                            color: '#27AE60'
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                            <p style={{ fontSize: '14px', color: '#4A5A6B', margin: 0 }}>
                                Todos os projetos já foram iniciados!
                            </p>
                        </div>
                    )}
                    <button
                        style={{
                            marginTop: '15px', padding: '8px 16px', cursor: 'pointer', width: '100%',
                            backgroundColor: stats.projectsNotStarted.length > 0 ? '#E67E22' : '#27AE60',
                            color: '#fff', border: 'none', borderRadius: '4px'
                        }}
                        onClick={() => {
                            // Navega para a página de projetos com filtro de "não iniciados"
                            if (stats.projectsNotStarted.length > 0) {
                                window.location.href = '/projeto/desenvolvimento';
                            } else {
                                window.location.href = '/projeto/desenvolvimento';
                            }
                        }}
                    >
                        {stats.projectsNotStarted.length > 0
                            ? 'Ver Projetos Não Iniciados'
                            : 'Ver Todos os Projetos'}
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default ComercialDashboard;